import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Heart, Eye, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Product } from '@/types/product';
import { useCart } from '@/contexts/CartContext';

type ProductVariant = Product['variants'][0];

interface ProductCardProps {
  product: Product;
  onAddToCart?: (product: Product, variant: ProductVariant) => void;
  onQuickView?: (product: Product) => void;
  className?: string;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToCart,
  onQuickView,
  className = ''
}) => {
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});
  const [isFavorited, setIsFavorited] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const { addToCart: addToCartContext } = useCart();

  React.useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get all available images for gallery
  const productImages = [product.images.main, ...product.images.gallery].filter(Boolean);
  const hasMultipleImages = productImages.length > 1;

  const handleAddToCart = async () => {
    // For color-only products, only check if color is selected
    const hasColors = product.variants.some(v => v.color);
    const hasSizes = product.variants.some(v => v.size);
    
    if ((hasSizes && !selectedSize) || (hasColors && !selectedColor)) {
      if (onQuickView) {
        onQuickView(product);
      }
      return;
    }

    setIsLoading(true);
    
    try {
      // Find the matching variant
      let matchingVariant;
      if (hasSizes && hasColors) {
        // Both size and color
        matchingVariant = product.variants.find(v => 
          v.size === selectedSize && v.color === selectedColor
        );
      } else if (hasColors) {
        // Color only
        matchingVariant = product.variants.find(v => v.color === selectedColor);
      } else if (hasSizes) {
        // Size only
        matchingVariant = product.variants.find(v => v.size === selectedSize);
      } else {
        // No variants
        matchingVariant = product.variants[0];
      }
      
      // Fallback to first variant if no match
      if (!matchingVariant) {
        matchingVariant = product.variants[0];
      }
      
      // Add to cart using context
      await addToCartContext(product, matchingVariant, 1);
      
      // Call the legacy onAddToCart prop if provided (for backwards compatibility)
      if (onAddToCart) {
        onAddToCart(product, matchingVariant);
      }
      
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageLoad = (imageSrc: string) => {
    setImageLoadingStates(prev => ({ ...prev, [imageSrc]: false }));
  };

  const handleImageLoadStart = (imageSrc: string) => {
    setImageLoadingStates(prev => ({ ...prev, [imageSrc]: true }));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const handleFavoriteToggle = () => {
    setIsFavorited(!isFavorited);
  };

  const sizeOptions = [...new Set(product.variants.map(v => v.size).filter(Boolean))];
  const colorOptions = [...new Set(product.variants.map(v => v.color).filter(Boolean))];
  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const discountPercentage = hasDiscount ? 
    Math.round(((product.originalPrice! - product.price) / product.originalPrice!) * 100) : 0;

  const currentImage = productImages[currentImageIndex];
  const isImageLoading = imageLoadingStates[currentImage];

  return (
    <motion.div
      ref={cardRef}
      className={`group relative bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden ${className}`}
      whileHover={{ y: -2 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      role="article"
      aria-labelledby={`product-${product.id}-title`}
    >
      {/* Product Image Gallery Container */}
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Loading State */}
        <AnimatePresence>
          {isImageLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-white flex items-center justify-center z-10"
            >
              <div className="w-8 h-8 border-4 border-gray-200 border-t-lime-400 rounded-full animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Product Image */}
        <motion.img
          key={currentImageIndex}
          src={currentImage}
          alt={`${product.name} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onLoadStart={() => handleImageLoadStart(currentImage)}
          onLoad={() => handleImageLoad(currentImage)}
                      onError={(e) => {
              // Fallback to placeholder if image fails to load
              const img = e.target as HTMLImageElement;
              if (!img.src.includes('placeholder')) {
                img.src = `https://via.placeholder.com/400x400/10b981/ffffff?text=${encodeURIComponent(product.name.replace(/\s+/g, '+'))}`;
              }
              handleImageLoad(currentImage);
            }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />

        {/* Image Gallery Navigation */}
        {hasMultipleImages && (
          <>
            {/* Desktop: Hover to show navigation */}
            {!isMobileView && (
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-md z-10"
                  onClick={prevImage}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-md z-10"
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Image Dots Indicator */}
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1 z-10">
              {productImages.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    index === currentImageIndex 
                      ? 'bg-lime-400 w-4' 
                      : 'bg-white/60 hover:bg-white/80'
                  }`}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Product Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
          {product.badge && (
            <Badge
              variant={product.badge === 'NEW' ? 'default' : 'secondary'}
              className={`text-xs font-bold shadow-md ${
                product.badge === 'NEW' 
                  ? 'bg-lime-400 text-black hover:bg-lime-500' 
                  : product.badge === 'BESTSELLER'
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : product.badge === 'PREMIUM'
                  ? 'bg-purple-500 text-white hover:bg-purple-600'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              {product.badge}
            </Badge>
          )}
          {hasDiscount && (
            <Badge variant="destructive" className="text-xs font-bold shadow-md bg-red-500 text-white hover:bg-red-600">
              -{discountPercentage}%
            </Badge>
          )}
        </div>

        {/* Action Buttons */}
        {isMobileView ? (
          /* Mobile: Always visible action buttons */
          <div className="absolute top-3 right-3 flex flex-col gap-2 z-10">
            <Button
              size="sm"
              variant="secondary"
              className="w-10 h-10 rounded-full bg-white/90 hover:bg-lime-400 text-gray-700 hover:text-black shadow-md"
              onClick={() => onQuickView?.(product)}
              aria-label={`Quick view ${product.name}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className={`w-10 h-10 rounded-full bg-white/90 shadow-md transition-colors duration-200 ${
                isFavorited 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'text-gray-700 hover:bg-red-500 hover:text-white'
              }`}
              onClick={handleFavoriteToggle}
              aria-label={isFavorited ? `Remove ${product.name} from favorites` : `Add ${product.name} to favorites`}
            >
              <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
            </Button>
          </div>
        ) : (
          /* Desktop: Hover to show action buttons */
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col gap-2 z-10">
            <Button
              size="sm"
              variant="secondary"
              className="w-9 h-9 rounded-full bg-white/90 hover:bg-lime-400 text-gray-700 hover:text-black shadow-md"
              onClick={() => onQuickView?.(product)}
              aria-label={`Quick view ${product.name}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className={`w-9 h-9 rounded-full bg-white/90 shadow-md transition-colors duration-200 ${
                isFavorited 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'text-gray-700 hover:bg-red-500 hover:text-white'
              }`}
              onClick={handleFavoriteToggle}
              aria-label={isFavorited ? `Remove ${product.name} from favorites` : `Add ${product.name} to favorites`}
            >
              <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
            </Button>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 md:p-5">
        {/* Rating */}
        {product.rating && (
          <div className="flex items-center gap-1 mb-2" role="img" aria-label={`Rating: ${product.rating} out of 5 stars`}>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-3 h-3 ${
                    i < Math.floor(product.rating!)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                  aria-hidden="true"
                />
              ))}
            </div>
            <span className="text-xs text-gray-600 ml-1">
              ({product.reviewCount || 0} reviews)
            </span>
          </div>
        )}

        {/* Product Name */}
        <h3 
          id={`product-${product.id}-title`}
          className="font-semibold text-gray-900 text-sm md:text-base mb-2 line-clamp-2 leading-tight"
        >
          {product.name}
        </h3>

        {/* Category */}
        <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">
          {product.category}
        </p>

        {/* Price */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-lg md:text-xl font-bold text-gray-900">
            ${product.price}
          </span>
          {hasDiscount && (
            <span className="text-sm text-gray-500 line-through">
              ${product.originalPrice}
            </span>
          )}
        </div>

        {/* Variant Selectors */}
        <div className="space-y-3 mb-4">
          {/* Size Selector */}
          {sizeOptions.length > 0 && (
            <div>
              <label 
                htmlFor={`size-select-${product.id}`}
                className="text-xs font-semibold text-gray-900 mb-1 block"
              >
                Size
              </label>
              <Select value={selectedSize} onValueChange={setSelectedSize}>
                <SelectTrigger 
                  id={`size-select-${product.id}`}
                  className="w-full h-9 text-sm bg-white border-gray-300 text-gray-900 hover:border-lime-400 focus:border-lime-400 focus:ring-lime-400"
                  aria-label="Select product size"
                >
                  <SelectValue placeholder="Select size" className="text-gray-500" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg">
                  {sizeOptions.map((size) => (
                    <SelectItem key={size} value={size} className="text-sm text-gray-900 hover:bg-lime-50 hover:text-lime-900">
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Color Selector */}
          {colorOptions.length > 0 && (
            <div>
              <label 
                htmlFor={`color-select-${product.id}`}
                className="text-xs font-semibold text-gray-900 mb-1 block"
              >
                Color
              </label>
              <Select value={selectedColor} onValueChange={setSelectedColor}>
                <SelectTrigger 
                  id={`color-select-${product.id}`}
                  className="w-full h-9 text-sm bg-white border-gray-300 text-gray-900 hover:border-lime-400 focus:border-lime-400 focus:ring-lime-400"
                  aria-label="Select product color"
                >
                  <SelectValue placeholder="Select color" className="text-gray-500" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg">
                  {colorOptions.map((color) => (
                    <SelectItem key={color} value={color} className="text-sm text-gray-900 hover:bg-lime-50 hover:text-lime-900">
                      {color}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Add to Cart Button - Enhanced for mobile */}
        <Button
          onClick={handleAddToCart}
          disabled={isLoading}
          className={`w-full bg-lime-400 hover:bg-lime-500 text-black font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
            isMobileView ? 'h-12 text-base' : 'h-10 text-sm'
          }`}
          aria-label={`Add ${product.name} to cart`}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Adding...
            </div>
          ) : (!selectedSize || (colorOptions.length > 0 && !selectedColor)) ? (
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Quick View
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              Add to Cart
            </div>
          )}
        </Button>

        {/* Selection Hint */}
        {(!selectedSize || (colorOptions.length > 0 && !selectedColor)) && (
          <p className="text-xs text-lime-700 mt-2 text-center font-medium bg-lime-50 py-1 px-2 rounded-md">
            Click "Quick View" to select {!selectedSize ? 'size' : 'color'} and add to cart
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard; 